import { defaultMemoize } from 'reselect';

import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { UserIdentityConflictDefinition } from '~src/authentication/contentDefinitions/UserIdentityConflictDefinition';
import { LabelValueDefinition } from '~src/catalog/components/contentDefinitions/LabelValueDefiniton';
import { CollectionDefinition } from '~src/Collections/contentDefinitions/CollectionDefinition';
import { ConnectedApplicationDefinition } from '~src/connectedApplications/content/ConnectedApplicationDefinition';
import { GeocodingTaskDefinition } from '~src/geocoding/contentDefinitions/GeocodingTaskDefinition';
import { ProjectDefinition } from '~src/projects/components/contentDefinitions/ProjectDefinition';
import { RecommendedViewDefinition } from '~src/recommendations/contentDefinitions/RecommendedViewDefinition';
import {
  setCollectionSpaceContentInOrder,
  setContentSpaceDefinitions,
  setExplorableItemDefinitions,
  setFavoriteMixedContentInOrder,
  setMixedContentInOrder,
  setPersonalSpaceContentInOrder,
  setRecycleBinMixedContentInOrder
} from '~src/spacesPlatform/contentDefinitionInterfaces/ContentDefinitionsLists';
import { ExplorableItemDefinition, MixedContentItemDefinition, SpaceDefinition } from '~src/spacesPlatform/contentDefinitionInterfaces/ContentDefinitionsTypes';
import {
  ExplorableItemType,
  SpaceInfoItemType,
} from '~src/spacesPlatform/models/ContentType';
import { ViewDefinition } from '~src/views/contentDefinitions/ViewDefinition';

import { BackgroundJobDefinition } from '../../backgroundJob/components/contentDefinitions/BackgroundJobDefinition';
import { DatabaseDefinition } from '../../catalog/components/contentDefinitions/DatabaseDefinition';
import { TableDefinition } from '../../catalog/components/contentDefinitions/TableDefinition';
import { ConnectedWorkbooksInDatasourceDefinition } from '../../datasource/components/contentDefinitions/ConnectedWorkbooksInDatasourceDefinition';
import { DatasourceDefinition } from '../../datasource/components/contentDefinitions/DatasourceDefinition';
import { DependentDataSourcesInDatasourceDefinition } from '../../datasource/components/contentDefinitions/DependentDataSourcesInDatasourceDefinition';
import { DataConnectionDefinition } from '../../datasource/dataConnection/components/contentDefinitions/DataConnectionDefinition';
import { DataConnectionInDatasourceDefinition } from '../../datasource/dataConnection/components/contentDefinitions/DataConnectionInDatasourceDefinition';
import { DataAlertDefinition } from '../../dda/contentDefinitions/DataAlertDefinition';
import { RemoteRefreshSchedulesDefinition } from '../../extractRefresh/contentDefinitions/RemoteRefreshSchedulesDefinition';
import { ExtractTaskDefinition } from '../../extractTask/components/spaces/contentDefinitions/ExtractTaskDefinition';
import { FlowDefinition } from '../../flow/contentDefinitions/FlowDefinition';
import { FlowOutputStepRunDefinition } from '../../flow/contentDefinitions/FlowOutputStepRunDefinition';
import { RunFlowTaskDefinition } from '../../flow/contentDefinitions/RunFlowTaskDefinition';
import { PublishedConnectionColumnDefinition } from '../../publishedConnection/contentDefinitions/PublishedConnectionColumnDefinition';
import { PublishedConnectionDefinition } from '../../publishedConnection/contentDefinitions/PublishedConnectionDefinition';
import { PublishedConnectionTableDefinition } from '../../publishedConnectionTable/contentDefinitions/PublishedConnectionTableDefinition';
import { ResourceGovernanceSettingDefiniton } from '../../schedule/components/contentDefinitions/ResourceGovernanceSettingDefinition';
import { ScheduleDefinition } from '../../schedule/components/contentDefinitions/ScheduleDefinition';
import { SiteDefinition } from '../../siteManagement/contentDefinitions/SiteDefinition';
import { SubscriptionDefinition } from '../../subscription/contentDefinitions/SubscriptionDefinition';
import { GroupDefinition } from '../../userManagement/groups/components/contentDefinitions/GroupDefinition';
import { GroupSetsDefinition } from '../../userManagement/groupsets/components/contentDefinitions/GroupSetDefinition';
import { ServerUserDefinition } from '../../userManagement/users/contentDefinition/ServerUserDefinition';
import { SiteUserDefinition } from '../../userManagement/users/contentDefinition/SiteUserDefinition';
import { CustomViewDefinition } from '../../views/contentDefinitions/customViews/CustomViewDefinition';
import { WorkbookDefinition } from '../../workbook/components/contentDefinitions/WorkbookDefinition';
import { SearchUserDefinition } from './SearchUserDefinition';

const mixedContentInOrder: MixedContentItemDefinition[] = [
  ProjectDefinition,
  WorkbookDefinition,
  ViewDefinition,
  DatasourceDefinition,
  FlowDefinition,
  PublishedConnectionDefinition,
];

const collectionSpaceContentInOrder: MixedContentItemDefinition[] = [
  ProjectDefinition,
  WorkbookDefinition,
  ViewDefinition,
  DatasourceDefinition,
  FlowDefinition,
  PublishedConnectionDefinition,
  PublishedConnectionTableDefinition,
];

const personalSpaceContentInOrder: MixedContentItemDefinition[] = [
  WorkbookDefinition,
];

const favoriteMixedContentInOrder: MixedContentItemDefinition[] = [
  ProjectDefinition,
  WorkbookDefinition,
  ViewDefinition,
  DatasourceDefinition,
  FlowDefinition,
  CollectionDefinition,
  PublishedConnectionDefinition,
  PublishedConnectionTableDefinition
];

const recycleBinMixedContentInOrder: MixedContentItemDefinition[] = [
  ProjectDefinition,
  WorkbookDefinition,
  DatasourceDefinition
];

const explorableItemDefinitions: Record<
  ExplorableItemType,
  ExplorableItemDefinition
> = {
  addressGeocodingTask: GeocodingTaskDefinition,
  backgroundJob: BackgroundJobDefinition,
  collection: CollectionDefinition,
  connectedApplication: ConnectedApplicationDefinition,
  connectedWorkbook: ConnectedWorkbooksInDatasourceDefinition,
  dependentDataSource: DependentDataSourcesInDatasourceDefinition,
  connection: DataConnectionInDatasourceDefinition,
  customView: CustomViewDefinition,
  dataalert: DataAlertDefinition,
  database: DatabaseDefinition,
  dataconnection: DataConnectionDefinition,
  datasource: DatasourceDefinition,
  extractTask: ExtractTaskDefinition,
  flow: FlowDefinition,
  flowOutputStepRun: FlowOutputStepRunDefinition,
  group: GroupDefinition,
  groupset: GroupSetsDefinition,
  project: ProjectDefinition,
  virtualConnection: PublishedConnectionDefinition,
  virtualConnectionTable: PublishedConnectionTableDefinition,
  virtualConnectionColumn: PublishedConnectionColumnDefinition,
  recommendedView: RecommendedViewDefinition,
  remoteRefreshSchedule: RemoteRefreshSchedulesDefinition,
  resourceGovernanceSetting: ResourceGovernanceSettingDefiniton,
  runFlowTask: RunFlowTaskDefinition,
  schedule: ScheduleDefinition,
  searchuser: SearchUserDefinition,
  serveruser: ServerUserDefinition,
  userIdentityConflict: UserIdentityConflictDefinition,
  site: SiteDefinition,
  siteuser: SiteUserDefinition,
  subscription: SubscriptionDefinition,
  table: TableDefinition,
  view: ViewDefinition,
  workbook: WorkbookDefinition,
  labelValue: LabelValueDefinition
};

const contentSpaceDefinitions: {
  [C in SpaceInfoItemType]: SpaceDefinition<C>;
} = {
  collection: CollectionDefinition,
  connectedApplication: ConnectedApplicationDefinition,
  database: DatabaseDefinition,
  datasource: DatasourceDefinition,
  flow: FlowDefinition,
  group: GroupDefinition,
  groupset: GroupSetsDefinition,
  project: ProjectDefinition,
  virtualConnection: PublishedConnectionDefinition,
  virtualConnectionTable: PublishedConnectionTableDefinition,
  resourceGovernanceSetting: ResourceGovernanceSettingDefiniton,
  schedule: ScheduleDefinition,
  serveruser: ServerUserDefinition,
  siteuser: SiteUserDefinition,
  view: ViewDefinition,
  workbook: WorkbookDefinition,
  table: TableDefinition,
  labelValue: LabelValueDefinition
};

// NOTE: FF values can be overriden at runtime, and they need to be passed as parameters
// so that the evaluations for content definition lists can occur at runtime (DEFECT 1412349).
// Memoization is also needed here in order to prevent unnecessary calls when these
// content definition lists are used as dependencies of other memoized methods.
// For example, `FilterByTypePanel` has such method which refetches content type counts
// whenever dependencies change. Without memoization, new lists would be returned here
// even when FF values don't change, leading to unnecessary calls to fetch counts.
const mixedContentInOrderWithFFGatedContentDefinition = (isDatabaseEnabled: boolean) => [
  ...mixedContentInOrder,
  ...isDatabaseEnabled ? [DatabaseDefinition] : [],
];
const memoizedMixedContentInOrderWithFFGatedContentDefinition = defaultMemoize(mixedContentInOrderWithFFGatedContentDefinition);
setMixedContentInOrder(undefined, () => memoizedMixedContentInOrderWithFFGatedContentDefinition(
  FeatureFlags.databasesInProjectsEnabled && FeatureFlags.databasesInProjectsPostMVPEnabled
));

const collectionSpaceContentInOrderWithFFGatedContentDefinition = () => [
  ...collectionSpaceContentInOrder,
];
const memoizedCollectionSpaceContentInOrderWithFFGatedContentDefinition = defaultMemoize(collectionSpaceContentInOrderWithFFGatedContentDefinition);
setCollectionSpaceContentInOrder(undefined, () => memoizedCollectionSpaceContentInOrderWithFFGatedContentDefinition());

const personalSpaceContentInOrderWithFFGatedContentDefinition = () => [
  ...personalSpaceContentInOrder,
];
const memoizedPersonalSpaceContentInOrderWithFFGatedContentDefinition = defaultMemoize(personalSpaceContentInOrderWithFFGatedContentDefinition);
setPersonalSpaceContentInOrder(undefined, () => memoizedPersonalSpaceContentInOrderWithFFGatedContentDefinition());

const recycleBinSpaceContentInOrderWithFFGatedContentDefinition = () => [
  ...recycleBinMixedContentInOrder,
];
const memoizedRecycleBinSpaceContentInOrderWithFFGatedContentDefinition = defaultMemoize(recycleBinSpaceContentInOrderWithFFGatedContentDefinition);
setRecycleBinMixedContentInOrder(undefined, () => memoizedRecycleBinSpaceContentInOrderWithFFGatedContentDefinition());


const favoriteSpaceContentInOrderWithFFGatedContentDefinition = () => [
  ...favoriteMixedContentInOrder,
];
const memoizedFavoriteSpaceContentInOrderWithFFGatedContentDefinition = defaultMemoize(favoriteSpaceContentInOrderWithFFGatedContentDefinition);
setFavoriteMixedContentInOrder(undefined, () => memoizedFavoriteSpaceContentInOrderWithFFGatedContentDefinition());

setExplorableItemDefinitions(explorableItemDefinitions);
setContentSpaceDefinitions(contentSpaceDefinitions);